// S25: Admin Overview Page
import { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Space,
  Alert,
  Table,
  Tag,
  Button,
  Typography,
  message,
} from 'antd';
import {
  DollarOutlined,
  TeamOutlined,
  LineChartOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  RightOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import {
  adminOverviewEndpoints,
  type AdminOverview,
} from '../api/adminOverviewEndpoints';
import type { AuditLog } from '../api/auditLogEndpoints';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

export function AdminOverview() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOverview();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchOverview, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchOverview = async () => {
    try {
      const data = await adminOverviewEndpoints.getOverview();
      setOverview(data);
    } catch (error: any) {
      console.error('Failed to fetch admin overview:', error);
      message.error('Failed to load admin overview');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(cents / 100);
  };

  const getEventColor = (event: string | null | undefined): string => {
    if (!event) return 'default';

    if (
      event.includes('failed') ||
      event.includes('rejected') ||
      event.includes('expired')
    ) {
      return 'red';
    }
    if (
      event.includes('approved') ||
      event.includes('succeeded') ||
      event.includes('completed') ||
      event.includes('consumed')
    ) {
      return 'green';
    }
    if (
      event.includes('started') ||
      event.includes('scheduled') ||
      event.includes('requested')
    ) {
      return 'blue';
    }
    return 'default';
  };

  const activityColumns: ColumnsType<AuditLog> = [
    {
      title: 'Time',
      dataIndex: 'at',
      key: 'at',
      width: 150,
      render: (at: string) => (
        <Text type="secondary">{dayjs(at).fromNow()}</Text>
      ),
    },
    {
      title: 'Event',
      dataIndex: 'event',
      key: 'event',
      render: (event: string) => (
        <Tag color={getEventColor(event)}>{event}</Tag>
      ),
    },
    {
      title: 'Entity',
      key: 'entity',
      render: (_, record) => (
        <Space orientation="vertical" size={0}>
          <Text strong>{record.entity_type}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.entity_id ? record.entity_id.substring(0, 8) : 'N/A'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Actor',
      dataIndex: 'actor_name',
      key: 'actor_name',
      render: (name: string, record) => (
        <Text>{record.actor_type === 'system' ? 'System' : name}</Text>
      ),
    },
  ];

  const hasOpenActions =
    overview &&
    (overview.pending_withdrawals_count > 0 ||
      overview.approved_withdrawals_count > 0 ||
      overview.undistributed_profit_entries_count > 0);

  return (
    <div>
      <PageHeader
        title="Admin Overview"
        description="System status and open actions dashboard"
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchOverview}
            loading={loading}
          >
            Refresh
          </Button>
        }
      />

      {/* Summary Tiles */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Pool"
              value={overview?.total_pool_cents || 0}
              precision={2}
              prefix={<DollarOutlined />}
              formatter={(value) => formatCurrency(Number(value))}
              loading={loading}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Contributors"
              value={overview?.active_contributor_count || 0}
              prefix={<TeamOutlined />}
              loading={loading}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Last Profit Entry"
              value={
                overview?.last_profit_entry
                  ? formatCurrency(overview.last_profit_entry.gross_cents)
                  : 'None'
              }
              prefix={<LineChartOutlined />}
              loading={loading}
            />
            {overview?.last_profit_entry && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {dayjs(overview.last_profit_entry.created_at).fromNow()}
              </Text>
            )}
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Last Weekly Settlement"
              value={
                overview?.last_weekly_settlement
                  ? dayjs(overview.last_weekly_settlement.week_ending_at).format(
                      'MMM D'
                    )
                  : 'None'
              }
              prefix={<CalendarOutlined />}
              loading={loading}
            />
            {overview?.last_weekly_settlement && (
              <Space size={4}>
                <Tag
                  color={
                    overview.last_weekly_settlement.status === 'completed'
                      ? 'green'
                      : overview.last_weekly_settlement.status === 'failed'
                      ? 'red'
                      : 'blue'
                  }
                  style={{ marginTop: 8 }}
                >
                  {overview.last_weekly_settlement.status}
                </Tag>
              </Space>
            )}
          </Card>
        </Col>
      </Row>

      {/* Open Actions Panel */}
      <Card
        title={
          <Space>
            <ClockCircleOutlined />
            <span>Open Actions</span>
            {hasOpenActions && (
              <Tag color="warning" icon={<WarningOutlined />}>
                Requires Attention
              </Tag>
            )}
          </Space>
        }
        style={{ marginBottom: 24 }}
        loading={loading}
      >
        {!hasOpenActions && overview && (
          <Alert
            title="All Clear"
            description="No pending actions at this time."
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
          />
        )}

        {hasOpenActions && (
          <Space orientation="vertical" size="large" style={{ width: '100%' }}>
            {/* Pending Withdrawals */}
            {overview && overview.pending_withdrawals_count > 0 && (
              <Card size="small" type="inner">
                <Row justify="space-between" align="middle">
                  <Col>
                    <Space orientation="vertical" size={0}>
                      <Title level={5} style={{ margin: 0 }}>
                        Pending Withdrawals
                      </Title>
                      <Space>
                        <Tag color="orange">
                          {overview.pending_withdrawals_count} requests
                        </Tag>
                        <Text strong>
                          {formatCurrency(
                            overview.pending_withdrawals_total_cents
                          )}
                        </Text>
                      </Space>
                    </Space>
                  </Col>
                  <Col>
                    <Button
                      type="primary"
                      icon={<RightOutlined />}
                      onClick={() => navigate('/admin/withdrawals')}
                    >
                      Review Queue
                    </Button>
                  </Col>
                </Row>
              </Card>
            )}

            {/* Approved Withdrawals */}
            {overview && overview.approved_withdrawals_count > 0 && (
              <Card size="small" type="inner">
                <Row justify="space-between" align="middle">
                  <Col>
                    <Space orientation="vertical" size={0}>
                      <Title level={5} style={{ margin: 0 }}>
                        Approved (Not Done) Withdrawals
                      </Title>
                      <Space>
                        <Tag color="blue">
                          {overview.approved_withdrawals_count} requests
                        </Tag>
                        <Text strong>
                          {formatCurrency(
                            overview.approved_withdrawals_total_cents
                          )}
                        </Text>
                      </Space>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Awaiting liquidity fulfillment
                      </Text>
                    </Space>
                  </Col>
                  <Col>
                    <Button
                      icon={<RightOutlined />}
                      onClick={() => navigate('/admin/withdrawals')}
                    >
                      View Queue
                    </Button>
                  </Col>
                </Row>
              </Card>
            )}

            {/* Undistributed Profit Entries */}
            {overview && overview.undistributed_profit_entries_count > 0 && (
              <Card size="small" type="inner">
                <Row justify="space-between" align="middle">
                  <Col>
                    <Space orientation="vertical" size={0}>
                      <Title level={5} style={{ margin: 0 }}>
                        Undistributed Profit Entries
                      </Title>
                      <Tag color="purple">
                        {overview.undistributed_profit_entries_count} entries
                      </Tag>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Ready for allocation
                      </Text>
                    </Space>
                  </Col>
                  <Col>
                    <Button
                      icon={<RightOutlined />}
                      onClick={() => navigate('/admin/profits')}
                    >
                      Allocate Now
                    </Button>
                  </Col>
                </Row>
              </Card>
            )}
          </Space>
        )}
      </Card>

      {/* Recent Activity Feed */}
      <Card
        title={
          <Space>
            <span>Recent Activity</span>
            <Tag>{overview?.recent_audit_events?.length || 0} events</Tag>
          </Space>
        }
        extra={
          <Button
            type="link"
            icon={<RightOutlined />}
            onClick={() => navigate('/admin/audit-logs')}
          >
            View All Audit Logs
          </Button>
        }
        loading={loading}
      >
        <Table
          columns={activityColumns}
          dataSource={overview?.recent_audit_events || []}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
}
